
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Landing from "./pages/Landing";
import WhoWeAre from "./pages/WhoWeAre";
import HowItWorks from "./pages/HowItWorks";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import Onboarding from "./pages/Onboarding";
import LearnerLayout from "./pages/learner/Layout";
import LearnerHome from "./pages/learner/Home";
import Learn from "./pages/learner/Learn";
import LearnDashboard from "./pages/learner/learn/LearnDashboard";
import TopicPage from "./pages/learner/learn/TopicPage";
import ModulePage from "./pages/learner/learn/ModulePage";
import Play from "./pages/learner/Play";
import Leaderboards from "./pages/learner/Leaderboards";
import Invest from "./pages/learner/Invest";
import Save from "./pages/learner/Save";
import Track from "./pages/learner/Track";
import Community from "./pages/learner/Community";
import CommunityPostDetail from "./pages/learner/CommunityPostDetail";
import Profile from "./pages/learner/Profile";
import Rewards from "./pages/learner/Rewards";
import Friends from "./pages/learner/Friends";
import { MoneyQuizPage } from "./pages/MoneyQuizPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Error getting session:", sessionError);
          setIsAuthenticated(false);
          return;
        }

        console.log("Public route initial session check:", session ? "Session found" : "No session");
        setIsAuthenticated(!!session);

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log("Auth state changed in public route:", event, session?.user?.id);
          setIsAuthenticated(!!session);
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error in auth initialization:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    console.log("User is authenticated, redirecting to home");
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Error getting session:", sessionError);
          if (mounted) {
            setIsAuthenticated(false);
            setIsLoading(false);
          }
          return;
        }

        console.log("Protected route initial session check:", session ? "Session found" : "No session");
        
        if (!session) {
          if (mounted) {
            setIsAuthenticated(false);
            setIsLoading(false);
          }
          return;
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log("Auth state changed in protected route:", event, session?.user?.id);
          
          if (event === 'SIGNED_OUT') {
            console.log("User signed out");
            if (mounted) {
              setIsAuthenticated(false);
            }
          } else if (event === 'SIGNED_IN') {
            console.log("User signed in");
            if (mounted) {
              setIsAuthenticated(true);
            }
          } else if (event === 'TOKEN_REFRESHED') {
            console.log("Token refreshed successfully");
            if (mounted) {
              setIsAuthenticated(true);
            }
          }
        });

        if (mounted) {
          setIsAuthenticated(true);
          setIsLoading(false);
        }

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error in protected route auth initialization:", error);
        if (mounted) {
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log("User not authenticated, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      
      <BrowserRouter>
        <Routes>
          <Route path="/" element={
            <PublicRoute>
              <Landing />
            </PublicRoute>
          } />
          <Route path="/who-we-are" element={
            <PublicRoute>
              <WhoWeAre />
            </PublicRoute>
          } />
          <Route path="/how-it-works" element={
            <PublicRoute>
              <HowItWorks />
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/signup" element={
            <PublicRoute>
              <SignUp />
            </PublicRoute>
          } />
          <Route path="/quiz" element={
            <PublicRoute>
              <MoneyQuizPage />
            </PublicRoute>
          } />
          
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          
          <Route
            element={
              <ProtectedRoute>
                <LearnerLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/home" element={<LearnerHome />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/dashboard" element={<LearnDashboard />} />
            <Route path="/learn/topic/:topicId" element={<TopicPage />} />
            <Route path="/learn/topic/:topicId/module/:moduleId" element={<ModulePage />} />
            <Route path="/play" element={<Play />} />
            <Route path="/leaderboards" element={<Leaderboards />} />
            <Route path="/friends" element={<Friends />} />
            <Route path="/invest" element={<Invest />} />
            <Route path="/save" element={<Save />} />
            <Route path="/track" element={<Track />} />
            <Route path="/rewards" element={<Rewards />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community/post/:postId" element={<CommunityPostDetail />} />
            <Route path="/profile" element={<Profile />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
